import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	Step,
	StepLabel,
	Stepper,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { style } from "./style";
import { DateChangeStep } from "./DateChangeStep";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Spacer } from "../../common/components/Spacer";
import { useHorizonChanges } from "../../common/hooks/useHandleHorizonChanges";
import { useRtkQueryDynamicEndpoint } from "../../common/hooks/useRtkQueryDynamicEndpoint";
import {
	useLazyGetContractPeriodHorizonChangeQuery,
	useUpdateContractPeriodHorizonChangeMutation,
} from "../../requests_cm/gecoContractsService/service";
import {
	ContractDetailType,
	ContractPeriodType,
	ErrorType,
	GetHorizonChangeParams,
} from "../../requests_cm/gecoContractsService/types";
import { formatApiErrorMessage } from "../../common/utils/formatApiErrorMessage";
import HorizonMonitoring from "./HorizonMonitoringStep";
import UpdateResultStep from "./UpdateResultStep";
import { useChangeRequest } from "../../requests_cm/gepoChangeRequestService/useChangeRequest";
import { useSearchParams } from "react-router-dom";
import { Groups } from "../authentication/authentication.constant";
import { useIsUserWithinGroups } from "../../common/hooks/useIsUserWithinGroups";
import { horizonChangeToChangeRequestMapper } from "../../requests_cm/gepoChangeRequestService/utils";
import { ChangeRequestResourceType } from "../../requests_cm/gepoChangeRequestService/types";

export interface HorizonChangeProps {
	contract: ContractDetailType | undefined;
	contractPeriod: ContractPeriodType | undefined;
	isOpen: boolean;
	setHorizonChangeOpen: (open: boolean) => void;
	onNivagateToChangeRequest: () => void;
}

export const HorizonChange = ({
	contract,
	contractPeriod,
	isOpen,
	setHorizonChangeOpen,
	onNivagateToChangeRequest,
}: HorizonChangeProps) => {
	const [searchParams] = useSearchParams();

	const changeRequestId = searchParams.get("changeRequestId");

	const { isUserAuthorized } = useIsUserWithinGroups();

	const isUserTrader = useMemo(() => {
		return isUserAuthorized([Groups.geco_trader]);
	}, [isUserAuthorized]);

	const [currentStep, setCurrentStep] = useState(0);
	const [currentHorizonParameters, setCurrentHorizonParameters] = useState<
		GetHorizonChangeParams | undefined
	>();
	const [
		baseUpdateHorizonChange,
		{
			error: errorUpdate,
			isLoading: isUpdateLoading,
			isSuccess: isUpdateSuccess,
		},
	] = useUpdateContractPeriodHorizonChangeMutation();
	const [
		baseGetHorizonChange,
		{
			data: proposedHorizonChanges,
			error: errorGet,
			isLoading: isFetchLoading,
		},
	] = useLazyGetContractPeriodHorizonChangeQuery();

	const { upsertChangeRequest, changeRequest, isLoading: isCreatingChangeRequest } = useChangeRequest(
		changeRequestId ? Number(changeRequestId) : undefined,
		{}
	);

	const getHorizonChange = useRtkQueryDynamicEndpoint(baseGetHorizonChange);
	const updateHorizonChange = useRtkQueryDynamicEndpoint(
		baseUpdateHorizonChange
	);

	const horizonChangePropsal = useMemo(() => {
		return (
			changeRequest?.raw_json ||
			proposedHorizonChanges?.proposed_changes ||
			[]
		);
	}, [changeRequest, proposedHorizonChanges]);

	const { entities, entitiesMap, updatedHorizonChanges, updateSingleEntity } =
		useHorizonChanges(horizonChangePropsal || []);

	useEffect(() => {
		if (currentHorizonParameters) {
			getHorizonChange({
				...currentHorizonParameters,
				old_value: currentHorizonParameters.old_value,
				new_value: currentHorizonParameters.new_value,
			});
		}
	}, [currentHorizonParameters]);

	useEffect(() => {
		if (
			changeRequest?.resource_type ===
			ChangeRequestResourceType.HORIZON_CHANGE
		) {
			setCurrentStep(1);
			setHorizonChangeOpen(true);
		}
	}, [changeRequest]);

	useEffect(() => {
		if (proposedHorizonChanges) {
			setCurrentStep(1);
		}
	}, [proposedHorizonChanges]);

	useEffect(() => {
		// reset on close
		if (!isOpen) {
			setCurrentStep(0);
			setCurrentHorizonParameters(undefined);
		}
	}, [isOpen]);

	useEffect(() => {
		if (isUpdateLoading) {
			setCurrentStep(2);
		}
	}, [isUpdateLoading]);

	const checkForHorizonChange = ({
		field_name,
		old_value,
		new_value,
		turn_safety_off,
		realign_related_contracts,
	}: Partial<GetHorizonChangeParams>) => {
		setCurrentHorizonParameters({
			contract_period_id: contractPeriod?.id!,
			field_name: field_name!,
			old_value: old_value!,
			new_value: new_value!,
			turn_safety_off: !!turn_safety_off,
			realign_related_contracts: !!realign_related_contracts,
		});
	};

	const confirmHorizonChanges = useCallback(async () => {
		if (isUserTrader) {
			updateHorizonChange({
				contractPeriod_id: contractPeriod?.id!,
				proposed_changes: updatedHorizonChanges,
			});
		} else {
			const changeRequestUpdate = await upsertChangeRequest(
				horizonChangeToChangeRequestMapper(
					updatedHorizonChanges,
					contract as ContractDetailType,
					contractPeriod!.id
				)
			);

			if (changeRequestUpdate?.errors) {
				enqueueSnackbar(changeRequestUpdate?.errors, {
					variant: "error",
					autoHideDuration: 3000,
				});
				return;
			}
			enqueueSnackbar("Horizon Change request  has been sent", {
				variant: "success",
				autoHideDuration: 3000,
			});
			onNivagateToChangeRequest();
		}
	}, [updatedHorizonChanges, contractPeriod, contract]);

	return (
		<Dialog
			maxWidth="lg"
			onClose={() => setHorizonChangeOpen(false)}
			open={isOpen}
		>
			<DialogTitle>{"Change Horizon of contract period"}</DialogTitle>
			<DialogContent>
				<Stepper activeStep={currentStep} sx={style.stepperHeader}>
					<Step key={0}>
						<StepLabel>Update Date Field</StepLabel>
					</Step>
					<Step key={1}>
						<StepLabel>Horizon Monitoring</StepLabel>
					</Step>
					<Step key={2}>
						<StepLabel>Update Horizon</StepLabel>
					</Step>
				</Stepper>
				{currentStep === 0 && (
					<Box sx={style.container}>
						<DateChangeStep
							onSubmit={checkForHorizonChange}
							onCancel={() => setHorizonChangeOpen(false)}
							contractPeriod={contractPeriod}
							error={formatApiErrorMessage(errorGet as ErrorType)}
							isLoading={isFetchLoading}
						/>
					</Box>
				)}
				{currentStep === 1 && (
					<Box sx={style.container}>
						<Spacer gap={24} />
						<HorizonMonitoring
							updateSingleEntity={updateSingleEntity}
							entities={entities}
							entitiesMap={entitiesMap}
							isLoading={isUpdateLoading || isCreatingChangeRequest}
							onConfirm={confirmHorizonChanges}
							confirmButtonLabel={
								!isUserTrader ? "Request changes" : ""
							}
							onGoBack={() => setCurrentStep(0)}
						/>
					</Box>
				)}
				{currentStep === 2 && (
					<Box sx={style.container}>
						<Spacer gap={24} />
						<UpdateResultStep
							onGoBack={() => setCurrentStep(1)}
							onClose={() => setHorizonChangeOpen(false)}
							errorMessage={formatApiErrorMessage(
								errorUpdate as ErrorType
							)}
							isLoading={isUpdateLoading}
							isSuccess={isUpdateSuccess}
						/>
					</Box>
				)}
			</DialogContent>
		</Dialog>
	);
};
